'use client';

import Image from 'next/image';
import React, { useEffect, useState } from 'react';

import {
    Alignable,
    AutoComplete,
    Box,
    BoxContent,
    Button,
    ButtonVariant,
    Column,
    Columns,
    IconName,
    ItemSelectedEvent,
    Link,
    ModalBody,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    TextMarkup,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyBold,
} from '@bytel/trilogy-react';

import { getPartnerProducts } from '@services/trade-in';

import { TradeInAutocompleteType, TradeInProductType } from '@app-types/trade-in';

import { InlinePrice } from '@components/inline-price';

import { TradeInStep } from '@constants/trade-in';

import recommerceIcon from '@assets/logo-recommerce.svg';
import {
    ReturnSessionTradeInProduct,
    clearSessionTradeInProduct,
    getSessionTradeInStep,
    setSessionTradeInStep,
} from '@product-detail/helpers';

export type MobileTradeInStep1Props = {
    step: TradeInStep;
    setStep: (value: TradeInStep) => void;
    productName: string;
    tradeInBonusPlan?: string;
    tradeInBonus: number;
    getQuestions: (productId: string) => void;
};

export function MobileTradeInStep1({
    step,
    setStep,
    productName,
    tradeInBonusPlan,
    tradeInBonus,
    getQuestions,
}: MobileTradeInStep1Props) {
    const [search, setSearch] = useState<string>('');
    const [selectedMobile, setSelectedMobile] = useState<TradeInProductType>();
    const [data, setData] = useState<TradeInAutocompleteType[]>([]);

    const handleSelectMobile = (mobile: ItemSelectedEvent<TradeInAutocompleteType | null>) => {
        if (!mobile.value) {
            return;
        }
        setSelectedMobile({
            name: mobile.value.label,
            brand: mobile.value.brand,
            data: mobile.value.data,
        });
        setSearch(mobile.value.label);
        getQuestions(mobile.value.data.id);
    };

    const handleResetSearch = () => {
        setSearch('');
        setSelectedMobile(undefined);
        clearSessionTradeInProduct();
    };

    const handleGoToForm = () => {
        if (!selectedMobile) {
            return;
        }
        setStep(TradeInStep.FORM);
        setSessionTradeInStep(TradeInStep.SEARCH, selectedMobile);
    };

    useEffect(() => {
        const valueStep = getSessionTradeInStep(TradeInStep.SEARCH) as ReturnSessionTradeInProduct[TradeInStep.SEARCH];
        if (!selectedMobile) {
            setSelectedMobile(valueStep);
            setSearch(valueStep?.name ?? '');
        }
    }, [step]);

    useEffect(() => {
        const controller = new AbortController();

        getPartnerProducts(search, controller).then((_data) => {
            setData(_data);
        });

        return () => {
            controller.abort('Search has changed');
        };
    }, [search]);

    if (step !== TradeInStep.SEARCH) return null;

    return (
        <ModalBody>
            <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                <Text markup={TextMarkup.SPAN}>Estimez votre ancien téléphone au meilleur prix</Text>
                {tradeInBonus > 0 && <Text markup={TextMarkup.SPAN}>&nbsp;et bénéficiez d’un bonus reprise</Text>}
            </Text>
            <Columns verticalAlign={Alignable.ALIGNED_CENTER}>
                <Column>
                    <AutoComplete
                        absoluteMenu
                        fullwidthMenu
                        data={data}
                        placeholder='Marque et modèle de votre ancien téléphone'
                        iconNameRight={search ? IconName.TIMES_CIRCLE : IconName.SEARCH}
                        onItemSelected={handleSelectMobile}
                        value={search}
                        onChange={({ inputValue }) => setSearch(inputValue)}
                        className='is-marginless'
                        onIconClick={handleResetSearch}
                        // Needed because data props change
                        matching={(data) => data}
                    />
                </Column>
                <Column>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        disabled={selectedMobile === undefined}
                        onClick={handleGoToForm}
                    >
                        Estimer
                    </Button>
                </Column>
            </Columns>
            <div>
                {/* la div permet d'avoir le css de la box dans la modal*/}
                <ModalContent bonus={tradeInBonus} productName={productName} tradeInBonusPlan={tradeInBonusPlan} />
                <Text level={TextLevels.TWO}>
                    <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                        Des questions ?
                    </Text>
                    <Text markup={TextMarkup.SPAN}>
                        &nbsp;Retrouvez les réponses de nos experts Bouygues Telecom sur&nbsp;
                    </Text>
                    <Text markup={TextMarkup.SPAN}>
                        <Link href='https://reprise-recommerce-bouygues.zendesk.com/hc/fr' blank>
                            notre FAQ
                        </Link>
                    </Text>
                </Text>
            </div>
        </ModalBody>
    );
}

type ModalContentProps = {
    bonus: number;
    productName: string;
    tradeInBonusPlan?: string;
};

function ModalContent({ bonus, productName, tradeInBonusPlan }: ModalContentProps) {
    return (
        <>
            <Box shadowless>
                <BoxContent backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                    <Title level={TitleLevels.FIVE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                        Comment ça marche ?
                    </Title>
                    <Step1 bonus={bonus} productName={productName} tradeInBonusPlan={tradeInBonusPlan} />
                    <Step2 />
                    <Step3 />
                    <Step4 bonus={bonus} />
                </BoxContent>
            </Box>
            <Spacer size={SpacerSize.THREE} />
        </>
    );
}

function Step1({
    bonus,
    productName,
    tradeInBonusPlan,
}: {
    bonus: number;
    productName: string;
    tradeInBonusPlan?: string;
}) {
    return (
        <>
            <Text level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                1. Estimez la valeur de votre ancien mobile
            </Text>
            {bonus > 0 && (
                <Text level={TextLevels.TWO}>
                    <Text markup={TextMarkup.SPAN}>et bénéficiez en plus d&apos;un bonus reprise&nbsp;</Text>
                    <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                        jusqu&apos;à&nbsp;
                        <InlinePrice amount={bonus} level={TextLevels.TWO} />
                    </Text>
                    <Text markup={TextMarkup.SPAN}>
                        pour l&apos;achat d&apos;un {productName} et la souscription ou renouvellement à un forfait
                        Bouygues Telecom {tradeInBonusPlan} (hors séries spéciales).
                    </Text>
                </Text>
            )}
        </>
    );
}

function Step2() {
    return (
        <Text level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
            2. Finalisez l&apos;achat de votre nouveau téléphone
        </Text>
    );
}

function Step3() {
    return (
        <>
            <Text level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                3. Envoyez votre ancien mobile gratuitement
            </Text>
            <Text level={TextLevels.TWO} marginless>
                a) Recevez à votre adresse de facturation (merci de la vérifier avant de valider votre commande) une
                enveloppe pré-affranchie et pré-adressée à notre partenaire&nbsp;
                <Image src={recommerceIcon} alt='recommerce' width={100} />
            </Text>
            <Text level={TextLevels.TWO}>
                b) Joignez à votre envoi le bon de cession, reçu par email après validation de votre reprise.
            </Text>
        </>
    );
}

function Step4({ bonus }: { bonus: number }) {
    return (
        <>
            <Text level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                4. Recevez le paiement de votre ancien mobile sous 48h
            </Text>
            <Text level={TextLevels.TWO} marginless>
                par virement bancaire après contrôle en atelier par notre partenaire&nbsp;
                <Image src={recommerceIcon} alt='recommerce' width={100} />
            </Text>
            {bonus > 0 && (
                <Text level={TextLevels.TWO}>
                    Le bonus reprise est lui déduit de votre 2 ème ou 3 ème facture Bouygues Telecom, si votre mobile
                    repris est validé à 10€ minimum.
                </Text>
            )}
        </>
    );
}
